/**
 * Hook useKanbanConversationPhaseStats - Busca estatísticas das fases do Kanban de Conversas
 * Utiliza RPC para contagem com filtros de permissão server-side
 * Versão: 1.0.0 - 2026-01-29
 * 
 * Segue padrão do usePipelinePhaseStats.ts (CRM) para consistência arquitetural
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { KanbanConversationFilters } from './useKanbanConversationColumn';

// Logger para depuração condicional
const logger = createLogger('KanbanConversationPhaseStats');

// Tipo das estatísticas da fase retornadas pelo RPC
export interface ConversationPhaseStats {
  phase_id: string;
  phase_name: string;
  phase_color: string | null;
  phase_position: number;
  is_default: boolean;
  conversation_count: number;
}

/**
 * Hook para buscar estatísticas (contadores) das fases de uma categoria
 * Utiliza RPC com filtros de permissão server-side
 * 
 * @param categoryId - ID da categoria do Kanban
 * @param filters - Filtros opcionais (mesmos do useKanbanConversationColumn)
 */
export function useKanbanConversationPhaseStats(
  categoryId: string | null,
  filters?: KanbanConversationFilters
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['kanban-conversation-phase-stats', categoryId, filters],
    queryFn: async () => {
      if (!categoryId) return [];

      logger.debug(`Buscando stats da categoria ${categoryId}`);

      // Chamada RPC com cast para unknown (função não tipada no types.ts)
      const { data, error } = await (supabase.rpc as any)('get_kanban_conversation_phase_stats', {
        p_category_id: categoryId,
        p_inbox_id: filters?.inboxId || null,
        p_agent_id: filters?.agentId || null,
        p_department_id: filters?.departmentId || null,
        p_read_status: filters?.readStatus === 'all' ? null : filters?.readStatus || null,
        p_search: filters?.search || null,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar stats das fases:', error);
        throw error;
      }

      const result = (data || []) as unknown as ConversationPhaseStats[];
      logger.debug(`Recebidas ${result.length} fases com stats`);
      return result;
    },
    enabled: !!categoryId && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  // Função para invalidar cache dos stats
  const invalidateStats = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['kanban-conversation-phase-stats', categoryId] 
    });
  };

  // Cria mapa para acesso rápido por phase_id
  const statsMap = new Map<string, number>();
  (query.data || []).forEach(stat => {
    statsMap.set(stat.phase_id, stat.conversation_count);
  });

  return {
    phases: query.data || [],
    statsMap,
    isLoading: query.isLoading,
    error: query.error,
    invalidateStats,
    refetch: query.refetch,
  };
}
